// Document Generator for Catastrophic Plan Template
import { format } from 'date-fns';
import { formatCurrency as fmtCur } from '../../utils/formatters';

/**
 * Generates a draft catastrophic plan document based on user preferences
 * @param {Object} formData - The data collected from the wizard schema
 * @returns {string} - Markdown formatted document that can be displayed in the preview
 */
function generateCatastrophicPlanDocument(formData = {}) {
  const currentDate = format(new Date(), 'MMMM dd, yyyy');
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return '';
    return fmtCur(amount);
  };
  
  // Ensure all required objects exist to prevent undefined errors
  const triggerEvents = formData.triggerEvents || {};
  const businessInfo = formData.businessInfo || {};
  const successorInfo = formData.successorInfo || {};
  const valuationMethodology = formData.valuationMethodology || {};
  const paymentTerms = formData.paymentTerms || {};
  const clientTransition = formData.clientTransition || {};
  const legalRegulatory = formData.legalRegulatory || {};
  const rightOfFirstRefusal = formData.rightOfFirstRefusal || {};
  const additionalProvisions = formData.additionalProvisions || {};
  const catastrophicPartner = formData.catastrophicPartner || {};
  
  // Generate trigger events text
  let triggerEventsText = [];
  if (triggerEvents.includeDeath) {
    triggerEventsText.push("death of Owner");
  }
  if (triggerEvents.includeDisability) {
    let disabilityText = "permanent disability of Owner";
    if (triggerEvents.disabilityDefinition === "unable_6_months") {
      disabilityText += " (defined as the inability to perform duties for 6 consecutive months)";
    } else if (triggerEvents.disabilityDefinition === "unable_12_months") {
      disabilityText += " (defined as the inability to perform duties for 12 consecutive months)";
    } else if (triggerEvents.disabilityDefinition === "physician_determination") {
      disabilityText += " (as determined by a licensed physician)";
    }
    triggerEventsText.push(disabilityText);
  }
  if (triggerEvents.includeRetirement) {
    let retirementText = "retirement of Owner";
    if (triggerEvents.retirementDefinition === "age_65") {
      retirementText += " (defined as reaching age 65)";
    } else if (triggerEvents.retirementDefinition === "age_67") {
      retirementText += " (defined as reaching age 67)";
    } else if (triggerEvents.retirementDefinition === "age_70") {
      retirementText += " (defined as reaching age 70)";
    } else if (triggerEvents.retirementDefinition === "owner_discretion") {
      retirementText += " (at Owner's discretion with appropriate notice)";
    }
    triggerEventsText.push(retirementText);
  }
  if (triggerEvents.includeSale) {
    triggerEventsText.push("voluntary sale of the business");
  }
  if (triggerEvents.otherTriggerEvents) {
    triggerEventsText.push(triggerEvents.otherTriggerEvents);
  }
  
  // Generate valuation method text
  let valuationText = "";
  switch(valuationMethodology.valuationMethod) {
    case "revenue_multiple":
      valuationText = `The purchase price shall be calculated as ${valuationMethodology.revenueMultiple || "[MULTIPLE]"} times the annual revenue of the business, calculated as the average annual revenue over the preceding ${valuationMethodology.revenueMultiple > 2 ? "three" : "two"} full calendar years prior to the triggering event.`;
      break;
    case "earnings_multiple":
      valuationText = `The purchase price shall be calculated as ${valuationMethodology.earningsMultiple || "[MULTIPLE]"} times the annual EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) of the business, calculated as the average annual EBITDA over the preceding ${valuationMethodology.earningsMultiple > 5 ? "three" : "two"} full calendar years prior to the triggering event.`;
      break;
    case "aum_percentage":
      valuationText = `The purchase price shall be calculated as ${valuationMethodology.aumPercentage || "[PERCENTAGE]"}% of the total Assets Under Management at the time of the triggering event.`;
      break;
    case "book_value":
      valuationText = `The purchase price shall be calculated as the book value of the business plus a premium of ${valuationMethodology.bookValuePremium || "[PERCENTAGE]"}% of the book value. Book value shall be determined by an accounting firm mutually agreed upon by the parties.`;
      break;
    case "independent_appraisal":
      if (valuationMethodology.appraisalMethod === "mutual_agreement") {
        valuationText = "The purchase price shall be determined by an independent business appraiser mutually agreed upon by Owner and Successor.";
      } else if (valuationMethodology.appraisalMethod === "separate_appraisers") {
        valuationText = "The purchase price shall be determined by two independent business appraisers, one selected by Owner and one selected by Successor. If the two appraisals differ by more than 10%, a third appraiser shall be jointly selected to provide a binding valuation.";
      } else if (valuationMethodology.appraisalMethod === "specified_firm") {
        valuationText = "The purchase price shall be determined by an independent business appraisal conducted by a qualified business valuation firm to be mutually specified in writing by the parties.";
      } else {
        valuationText = "The purchase price shall be determined by an independent business appraisal conducted by a qualified business valuation firm.";
      }
      break;
    case "agreed_value":
      valuationText = `The purchase price shall be the agreed value of ${formatCurrency(valuationMethodology.agreedValue) || "[AMOUNT]"}, which shall be updated ${valuationMethodology.valueUpdateFrequency === "annual" ? "annually" : valuationMethodology.valueUpdateFrequency === "biannual" ? "every two years" : "every three years"} in writing by mutual agreement of the parties.`;
      break;
    case "custom_formula":
      valuationText = `The purchase price shall be determined according to the following formula: ${valuationMethodology.customFormula || "[CUSTOM FORMULA TO BE INSERTED]"}`;
      break;
    default:
      valuationText = "The purchase price shall be determined by a method mutually agreed upon by the parties at the time of the triggering event.";
  }
  
  // Add inclusions/exclusions to valuation
  let valuationInclusions = [];
  if (valuationMethodology.includeTangibleAssets) {
    valuationInclusions.push("all tangible assets of the business");
  }
  if (valuationMethodology.includeAccounts) {
    valuationInclusions.push("the client accounts and book of business");
  }
  if (valuationMethodology.includeBrandValue) {
    valuationInclusions.push("the brand value and goodwill of the business");
  }
  
  if (valuationInclusions.length > 0) {
    valuationText += ` The valuation shall include ${valuationInclusions.join(", ")}.`;
  }
  
  if (valuationMethodology.excludedAssets) {
    valuationText += ` The following assets shall be specifically excluded from the valuation: ${valuationMethodology.excludedAssets}.`;
  }
  
  // Generate payment terms text
  let paymentTermsText = "";
  switch(paymentTerms.paymentStructure) {
    case "lump_sum":
      paymentTermsText = "The successor shall pay the purchase price in full at closing by wire transfer of immediately available funds.";
      break;
    case "down_installments":
      paymentTermsText = `The successor shall pay ${paymentTerms.downPaymentPercentage || "[PERCENTAGE]"}% of the purchase price as a down payment at closing, with the balance to be paid in equal ${paymentTerms.installmentFrequency || "monthly"} installments over a period of ${paymentTerms.installmentPeriod || "[NUMBER]"} years, with interest accruing on the unpaid balance at the rate of ${paymentTerms.interestRate || "[RATE]"}% per annum.`;
      break;
    case "all_installments":
      paymentTermsText = `The successor shall pay the purchase price in equal ${paymentTerms.installmentFrequency || "monthly"} installments over a period of ${paymentTerms.installmentPeriod || "[NUMBER]"} years, with interest accruing on the unpaid balance at the rate of ${paymentTerms.interestRate || "[RATE]"}% per annum.`;
      break;
    case "earn_out":
      let earnOutMetricText = "";
      if (paymentTerms.earnOutMetric === "revenue_retention") {
        earnOutMetricText = "client revenue retention";
      } else if (paymentTerms.earnOutMetric === "aum_retention") {
        earnOutMetricText = "Assets Under Management retention";
      } else if (paymentTerms.earnOutMetric === "client_count_retention") {
        earnOutMetricText = "client count retention";
      } else if (paymentTerms.earnOutMetric === "new_revenue") {
        earnOutMetricText = "new revenue generation";
      } else {
        earnOutMetricText = "the performance criteria specified in the final agreement";
      }
      
      paymentTermsText = `The purchase price shall be paid through an earn-out arrangement over a period of ${paymentTerms.earnOutPeriod || "[NUMBER]"} years, with payments based on ${earnOutMetricText}. Specific earn-out calculations shall be detailed in the final purchase agreement.`;
      break;
    default:
      paymentTermsText = "The payment terms shall be determined by mutual agreement of the parties at the time of the triggering event.";
  }
  
  // Add security information
  if (paymentTerms.securitization && paymentTerms.securitization !== "none") {
    let securityText = "";
    switch(paymentTerms.securitization) {
      case "business_assets":
        securityText = "a security interest in the business assets";
        break;
      case "personal_guarantee":
        securityText = "a personal guarantee from the Successor";
        break;
      case "life_insurance":
        securityText = "a life insurance policy on the Successor";
        break;
      case "escrow":
        securityText = "funds held in escrow";
        break;
      default:
        securityText = paymentTerms.securityDetails || "appropriate security as detailed in the final agreement";
    }
    
    paymentTermsText += ` The payment obligation shall be secured by ${securityText}.`;
  }
  
  // Generate client transition text
  let clientTransitionText = `The client transition period shall be ${clientTransition.transitionPeriod || "[NUMBER]"} months from the date of closing.`;
  
  // Add owner involvement details
  let ownerInvolvementText = "";
  switch(clientTransition.ownerInvolvement) {
    case "none":
      ownerInvolvementText = "The Owner shall have no obligation to remain involved in the business after closing.";
      break;
    case "limited":
      ownerInvolvementText = "The Owner shall provide limited consulting services to assist with client transition, not to exceed 10 hours per month during the transition period.";
      break;
    case "moderate":
      ownerInvolvementText = "The Owner shall remain involved on a part-time basis during the transition period to assist with client transition and business operations.";
      break;
    case "significant":
      ownerInvolvementText = "The Owner shall remain substantially involved in the business during the transition period to ensure smooth client transition and continuity of operations.";
      break;
    default:
      ownerInvolvementText = "The Owner's involvement during the transition period shall be determined by mutual agreement of the parties.";
  }
  clientTransitionText += ` ${ownerInvolvementText}`;
  
  // Add owner compensation details
  if (clientTransition.ownerCompensation && clientTransition.ownerCompensation !== "none") {
    let compensationText = "";
    switch(clientTransition.ownerCompensation) {
      case "hourly":
        compensationText = "an hourly consulting rate";
        break;
      case "flat_fee":
        compensationText = "a flat fee for the transition period";
        break;
      case "percentage":
        compensationText = "a percentage of revenue during the transition period";
        break;
      default:
        compensationText = "compensation as detailed in the final agreement";
    }
    
    if (clientTransition.compensationDetails) {
      compensationText += `: ${clientTransition.compensationDetails}`;
    }
    
    clientTransitionText += ` The Owner shall be compensated for transition services through ${compensationText}.`;
  }
  
  // Add client notification approach
  let notificationText = "";
  switch(clientTransition.clientNotification) {
    case "joint":
      notificationText = "a joint announcement from Owner and Successor";
      break;
    case "owner_first":
      notificationText = "an initial announcement from Owner followed by an introduction of the Successor";
      break;
    case "phased":
      notificationText = "a phased approach based on client segments";
      break;
    case "custom":
      notificationText = "the approach detailed in the final agreement";
      break;
    default:
      notificationText = "a mutually agreed approach";
  }
  clientTransitionText += ` Clients shall be notified of the transition through ${notificationText}.`;
  
  // Add client approval if needed
  if (clientTransition.clientApproval) {
    clientTransitionText += " Clients shall have the right to approve the transfer of their accounts to the Successor.";
  }
  
  // Add retention incentives if any
  if (clientTransition.retentionIncentives) {
    clientTransitionText += ` The following client retention incentives shall be implemented: ${clientTransition.retentionIncentives}.`;
  }
  
  // Generate legal and regulatory text
  let legalRegulatoryText = `This agreement shall be governed by the laws of ${legalRegulatory.governingLaw || "[JURISDICTION]"}.`;
  
  // Add regulatory approvals
  if (legalRegulatory.regulatoryApprovals && legalRegulatory.regulatoryApprovals.length > 0 && !legalRegulatory.regulatoryApprovals.includes("none")) {
    const approvals = [];
    
    if (legalRegulatory.regulatoryApprovals.includes("finra")) {
      approvals.push("FINRA");
    }
    if (legalRegulatory.regulatoryApprovals.includes("sec")) {
      approvals.push("SEC");
    }
    if (legalRegulatory.regulatoryApprovals.includes("state_insurance")) {
      approvals.push("State Insurance Commissioner");
    }
    if (legalRegulatory.regulatoryApprovals.includes("other") && legalRegulatory.otherApprovals) {
      approvals.push(legalRegulatory.otherApprovals);
    }
    
    if (approvals.length > 0) {
      legalRegulatoryText += ` The parties acknowledge that regulatory approvals may be required from the following: ${approvals.join(", ")}.`;
    }
  }
  
  // Add non-compete if applicable
  if (legalRegulatory.nonCompete) {
    legalRegulatoryText += ` Upon closing, Owner agrees not to compete with the business for a period of ${legalRegulatory.nonCompeteDuration || "[NUMBER]"} years`;
    
    if (legalRegulatory.nonCompeteGeography) {
      legalRegulatoryText += ` within ${legalRegulatory.nonCompeteGeography}`;
    }
    
    legalRegulatoryText += ".";
  }
  
  // Add dispute resolution
  let disputeResolutionText = "";
  switch(legalRegulatory.disputeResolution) {
    case "litigation":
      disputeResolutionText = "litigation in the appropriate courts";
      break;
    case "mediation_first":
      disputeResolutionText = "mandatory mediation before any litigation may be commenced";
      break;
    case "binding_arbitration":
      disputeResolutionText = "binding arbitration";
      break;
    case "mediation_arbitration":
      disputeResolutionText = "mediation followed by binding arbitration if mediation is unsuccessful";
      break;
    default:
      disputeResolutionText = "methods detailed in the final agreement";
  }
  legalRegulatoryText += ` Any disputes arising under this agreement shall be resolved through ${disputeResolutionText}.`;
  
  // Generate ROFR text
  let rofrText = "";
  if (rightOfFirstRefusal.includeROFR) {
    rofrText = `Owner grants Successor a right of first refusal to purchase the business. If Owner receives a bona fide offer from a third party that Owner intends to accept, Owner shall give Successor written notice of such offer. Successor shall have ${rightOfFirstRefusal.rofrNegotiationPeriod || "30"} days to negotiate with Owner. If the parties cannot reach agreement, and Owner receives a better offer from a third party, Successor shall have ${rightOfFirstRefusal.rofrMatchPeriod || "10"} days to match the terms of such offer.`;
  }
  
  // Generate additional provisions text
  let additionalProvisionsText = "";
  
  // Add key person insurance if applicable
  if (additionalProvisions.keyPersonInsurance) {
    additionalProvisionsText += `Owner and Successor shall each obtain key person insurance coverage in the amount of ${additionalProvisions.insuranceAmount || "an amount equal to the estimated business value"}.`;
  }
  
  // Add client access restrictions if any
  if (additionalProvisions.clientAccessRestrictions) {
    if (additionalProvisionsText) additionalProvisionsText += " ";
    additionalProvisionsText += `The following client access restrictions shall apply: ${additionalProvisions.clientAccessRestrictions}.`;
  }
  
  // Add intellectual property treatment if any
  if (additionalProvisions.intellectualProperty) {
    if (additionalProvisionsText) additionalProvisionsText += " ";
    additionalProvisionsText += `Intellectual property shall be treated as follows: ${additionalProvisions.intellectualProperty}.`;
  }
  
  // Add other provisions if any
  if (additionalProvisions.otherProvisions) {
    if (additionalProvisionsText) additionalProvisionsText += " ";
    additionalProvisionsText += `${additionalProvisions.otherProvisions}`;
  }
  
  // If the form is empty, return a simple welcome message
  if (Object.keys(formData).length === 0) {
    return '# Business Catastrophe Plan\n\nStart filling out the questionnaire to generate your Business Catastrophe Plan document.';
  }
  
  // Generate the full document as markdown
  const documentMarkdown = `
# BUSINESS CONTINUITY AGREEMENT

*DRAFT FOR REVIEW BY LEGAL COUNSEL*

**DRAFT - NOT FOR EXECUTION**

This agreement is entered into by and between the following parties:

**Owner:** ${businessInfo.ownerName || "___________________________"}, who has an office located at ${businessInfo.ownerAddress || "_______________________________________"}

and

**Successor:** ${successorInfo.hasSuccessor ? successorInfo.successorName || "___________________________" : "___________________________"}, who has an office located at ${successorInfo.hasSuccessor ? successorInfo.successorAddress || "_______________________________________" : "_______________________________________"}

(All notices, requests, demands, and other communications pertaining to this agreement shall be in writing and shall be deemed to have been duly given if delivered to either party at the addresses above.)

## 1. Agreement Description

The parties hereby agree that this Continuity Agreement is intended to:

a) provide for the transfer of Owner's business in the event of ${triggerEventsText.join(", ") || "specified trigger events"};

b) provide a means of determining the fair market value of the Owner's business;

c) provide guidelines as to the method of payment; and

d) enable Successor to continue to professionally service the client base of Owner's business after completion of final asset sale/purchase agreement.

In consideration of the mutual covenants and agreements contained herein, the parties agree as follows:

## 2. Right of First Refusal

${rofrText || "Owner hereby grants to Successor a right of first refusal to purchase the Owner's business on the terms and conditions contained herein in the event of the occurrence of one of the transfer events listed in Section 1 above."}

## 3. Termination of Agreement

The following events shall cause the termination of this Agreement:

a) The written notification by either party;

b) The dissolution, bankruptcy, or insolvency of either party's Business;

c) At such time as either party ceases to be appropriately licensed to carry out the obligations contained herein;

d) If any provisions of this agreement are deemed prohibited or unenforceable in any jurisdiction related to any party.

## 4. Purchase Price

The total purchase price of the Business being permanently conveyed to the Successor will be determined as follows:

${valuationText}

## 5. Payment Terms

${paymentTermsText}

## 6. Client Transition

${clientTransitionText}

## 7. Legal and Regulatory Considerations

${legalRegulatoryText}

${additionalProvisionsText ? `
## 8. Additional Provisions

${additionalProvisionsText}
` : ''}

## ${additionalProvisionsText ? '9' : '8'}. Transfer of Business

The transfer of Business shall take place as soon as possible after a transfer event or at such time as the parties hereto may agree. At the closing, a business acquisition agreement and other required instruments and documents shall be created and duly executed to complete the transfer of the business.

---

**IN WITNESS WHEREOF**, the parties hereto have executed this Agreement as of the date last written below.

**OWNER**

Signature: \_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_

Printed Name: ${businessInfo.ownerName || "\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_"}

Date: \_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_

**SUCCESSOR**

Signature: \_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_

Printed Name: ${successorInfo.hasSuccessor ? successorInfo.successorName || "\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_" : "\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_"}

Date: \_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_

---

*This Agreement is a template designed to provide general guidance to a wide variety of owners and potential successors and may not be suitable for all users or situations. Certain provisions in the Agreement may not be enforceable or applicable in all jurisdictions. This document is provided without any warranties whatsoever and the user hereby acknowledges that the provider is not qualified to provide and is not providing legal, accounting, tax or other professional advice or service in connection with the provision of this template.*

**IMPORTANT:** *This is a draft document generated based on your preferences in the wizard. Before signing, this document should be reviewed by qualified legal counsel with expertise in business succession planning for your specific industry and jurisdiction.*
`;
  
  return documentMarkdown;
}

// Export the document generator function
export default generateCatastrophicPlanDocument;
